<template>
  <div>
    <v-card>
      <v-data-table
        v-model="table.selected"
        :headers="table.headers"
        :items="locations ? locations.data : []"
        :server-items-length="totalItems"
        :items-per-page="locations && locations.meta ? parseInt(locations.meta.per_page) : 0"
        :page.sync="table.page"
        :options.sync="table.options"
        :sort-by.sync="table.sortBy"
        :sort-desc.sync="table.sortDesc"
        :loading="table.loading"
        item-key="id"
        show-select
        hide-default-footer
        @click:row="onClickRow"
      >
        <template v-slot:top>
          <v-row no-gutters class="pl-4 pr-4 pt-4">
            <v-col class="pr-md-4 pb-4 pb-md-0" cols="12" sm="12" md="6" xs="12">
              <v-select
                  v-model="filters.account"
                  :items="accounts.data"
                  :loading="loadingAccounts"
                item-text="account_name"
                  item-value="name"
                  menu-props="auto"
                  label="Conta"
                  hide-details
                  single-line
                  solo
                color="primary"
                  dense
              ></v-select>
            </v-col>
            <v-col cols="12" sm="12" md="6" xs="12">
              <v-text-field
                  v-model="filters.search"
                  append-icon="mdi-magnify"
                  clearable
                  label="Procurar"
                  single-line
                hide-details
                  outlined
                  dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row no-gutters class="pl-4 pr-4 pb-4">
            <v-col class="pr-sm-4 mt-4" cols="12" sm="6" md="4" xs="12">
              <v-autocomplete
                  autocomplete="off"
                  v-model="filters.state"
                  :search-input.sync="searchState"
                :loading="loadingStates"
                  :items="states.data"
                  item-text="administrative_area"
                item-value="administrative_area"
                  menu-props="auto"
                  label="Estado"
                  hide-details
                  single-line
                  multiple
                outlined
                  dense
                  clearable
              ></v-autocomplete>
            </v-col>
            <v-col class="pr-md-4 mt-4" cols="12" sm="6" md="4" xs="12">
              <v-autocomplete
                  autocomplete="off"
                  v-model="filters.city"
                  :loading="loadingCities"
                  :items="cities"
                item-text="locality"
                  item-value="locality"
                  menu-props="auto"
                  label="Cidade"
                  hide-details
                  single-line
                multiple
                  outlined
                  dense
                  clearable
              >
              </v-autocomplete>
            </v-col>

            <v-col class="mt-4" cols="12" sm="3" md="3" xs="3">
              <v-autocomplete
                autocomplete="off"
                v-model="limit"
                :items="[10, 25, 50, 100, 250, 500]"
                item-text="limit"
                item-value="limit"
                menu-props="auto"
                label="Número de registros"
                hide-details
                single-line
                outlined
                dense
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-row no-gutters class="pl-4 pr-4">
            <LocationStoreModal v-if="hasStoreLocationPermission" class="mr-2" :account-name="filters.account"
              :on-updated="fetchLocations" :disabled="cannotStoreLocation" />

            <LocationXlsImportModal class="mr-2" :permission="hasStoreLocationPermission" :accountNameXls="filters.account"
              :disabled="cannotImportXls" />

            <LocationTransferModal v-model="showTransferModal" :selected-locations="table.selected" :accounts="accounts.data" @transferred="fetchLocations"/>
            <v-btn small elevation="2" color="primary" v-on:click="applyFilters">
              Aplicar
            </v-btn>
          </v-row>

          <v-divider class="mt-3"></v-divider>

          <v-row v-if="table.selected.length > 0">
            <v-col>
              <div
                  class="text-subtitle-2 mt-2 pl-4"
                  v-text="`${countSelected} Selecionados`"
              >
              </div>
            </v-col>

            <v-col class="mr-4 mt-2">
              <v-select
                  v-model="table.action"
                  v-on:change="onChangeAction"
                  item-text="text"
                  item-value="value"
                :items="tableActions"
                  menu-props="auto"
                  label="Selecionar ação em massa"
                  hide-details
                  single-line
                  solo
                color="primary"
                  dense
              ></v-select>
            </v-col>
          </v-row>
        </template>

        <template v-slot:header.data-table-select="{ on, props }">
          <v-simple-checkbox
              color="info"
              :ripple="false"
              v-bind="props"
              v-on="on"
              v-model="table.allSelected"
              :indeterminate="table.unSelected.length > 0 "
              @change="toggleSelectAll"
          ></v-simple-checkbox>
        </template>

        <template v-slot:item.data-table-select="{ isSelected, select, item }">
          <v-simple-checkbox
              :ripple="false"
              :value="isSelected"
            @input="toggleRowItem(item, $event); select($event)"
          ></v-simple-checkbox>
        </template>

        <template v-slot:footer>
          <v-pagination
              total-visible="12"
              class="mb-4"
              v-model="table.page"
              :length="totalPages"
          ></v-pagination>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {handleResponseError} from '@/api/handleError'
import * as locationConstants from '@/constants/locations'
import arrayUnique from '@/helpers/arrayUnique'
import isNotEmpty from '@/helpers/isNotEmpty'
import {debounce, get} from 'lodash'
import LocationXlsImportModal from './Modals/LocationXlsImportModal.vue'
import LocationTransferModal from './Modals/LocationTransferModal.vue'
import LocationStoreModal from './Modals/LocationStoreModal.vue'

export default {
  name: 'Locations',
  computed: {
    ...mapState({
      locations: state => state.locations.all,
      accounts: state => state.accounts.all,
      cities: state => state.locations.cities,
      states: state => state.locations.states,
      bulk: state => state.locations.bulk,
      createExportLocation: state => state.locations.createExportLocation,
      user: state => state.auth.user
    }),
    /**
     * Total de locais carregados
     * @returns {number|number}
     */
    totalItems() {
      return this.locations && this.locations.meta ? parseInt(this.locations.meta.total) : 0
    },
    /**
     * Total de items selecionados
     * @returns {number|number}
     */
    countSelected() {
      return this.table.allSelected
        ? (this.locations && this.locations.meta ? parseInt(this.locations.meta.total) : 0) - this.table.unSelected.length
        : this.table.selected.length
    },
    /**
     * Lista de ações em massa
     * @returns {[{text: string, value: *}, {text: string, value: *}, {text: string, value: *}]}
     */
    tableActions() {
      if (!this.filters.account) {
        return
      }

      let account = this.accounts.data.find(a => a.name === this.filters.account)
      if (!account) []

      let options = [
        {text: 'Atualizar locais', value: locationConstants.UPDATE_LOCATION},
        {text: 'Criar postagem', value: locationConstants.CREATE_LOCAL_POST},
        {text: 'Editar postagem', value: locationConstants.UPDATE_LOCAL_POST},
      ]
      if (account.can_have_food_menus) {
        options.push({text: 'Atualizar cardápio', value: locationConstants.UPDATE_FOOD_MENU})
      }
      options.push({text: 'Transferir locais', value: locationConstants.TRANSFER_LOCATIONS})
      options.push({text: 'Exportar', value: locationConstants.EXPORT_LOCATIONS})
      return options
    },
    /**
     * Número de páginas
     * @returns {number|number}
     */
    totalPages() {
      return this.locations && this.locations.meta
        ? Math.ceil(parseInt(this.locations.meta.total) / parseInt(this.locations.meta.per_page))
        : 0
    }
  },
  components:{
    LocationXlsImportModal,
    LocationStoreModal,
    LocationTransferModal
  },
  props: {
    onUpdated: Function,
  },
  created() {
    this.fetchAccounts()
    this.setValuesFromUrl()

    const permissions = this.user.permissions
    Array.isArray(permissions) && permissions.forEach(item => {
      if(item.name === 'Adicionar locais') {
        this.hasStoreLocationPermission = true
      }
    })
  },
  destroyed() {
    this.$store.dispatch('locations/clearLocations')
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  },
  data: () => ({
    showTransferModal: false,
    cannotImportXls: true,
    cannotStoreLocation: true,
    loadingAccounts: false,
    loadingStates: false,
    loadingCities: false,
    loadingReport: false,
    searchState: '',
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
    filters: {
      search: '',
      account: null,
      state: null,
      city: null,
      params: {
        allSelected: 0,
        account: '',
        ids: [],
        accountName: '',
      },
    },
    table: {
      action: null,
      allSelected: false,
      selected: [],
      unSelected: [],
      page: 1,
      sortBy: null,
      sortDesc: false,
      options: {},
      loading: false,
      headers: [
        {text: 'Código', value: 'store_code', align: 'start'},
        {text: 'Nome', value: 'location_name'},
        {text: 'Endereço', value: 'address.address_line_one'},
        {text: 'Cidade', value: 'address.locality'},
        {text: 'Estado', value: 'address.administrative_area'},
        {text: 'Labels', value: 'labels', sortable: false},
        {text: 'Status', value: 'state'},
      ],
    },
    sortBy: '',
    sortDesc: '',
    page: 1,
    limit: 20,
    statesData: [],
    citiesData: [],
    appliedFilters: {},
    appliedParams: {},
    hasStoreLocationPermission: false,
    multiSelectPressed: false,
    lastSelectedIndex: null,
  }),
  methods: {
    async onChangeAction(event) {
      if (event === locationConstants.EXPORT_LOCATIONS) {
        await this.exportLocations()
        this.table.action = ''
      } else if (event === locationConstants.UPDATE_LOCATION) {
        this.dispatchBulk()
      } else if (event === locationConstants.UPDATE_LOCAL_POST) {
        this.dispatchBulk()
      } else if (event === locationConstants.CREATE_LOCAL_POST) {
        this.dispatchBulk()
      }else if (event === locationConstants.TRANSFER_LOCATIONS) {
      this.updateParamsFilters()
      this.showTransferModal = true
      this.table.action = ''
    }
    },
    handleKeyDown(event) {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.multiSelectPressed = true;
    }
    },
    handleKeyUp(event) {
      if (event.key === 'Control' || event.key === 'Meta') {
        this.multiSelectPressed = false;
      }
    },
    toggleSelectAll() {
      if (this.table.allSelected) {
        this.table.selected = [];
        this.table.unSelected = [];
        this.table.allSelected = false;
      } else {
        this.table.selected = this.locations.data.map(item => item.id);
        this.table.unSelected = [];
        this.table.allSelected = true;
      }
      this.updateParamsFilters();
    },
    /**
     * Exporta um arquivo xlsx com os locais selecionados
     */
    async exportLocations() {
      this.updateParamsFilters()

      this.loadingReport = true
      await this.$store.dispatch('locations/createExportLocations', this.filters).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao gerar o relatório.')
        this.showNotification(message, 'red darken-2')
        this.loadingReport = false
      })
      this.loadingReport = false
    },
    /**
     * Atualiza os filtros de atualização em massa
     */
    updateParamsFilters() {
      this.filters.params.allSelected = this.table.allSelected
      this.filters.params.account = this.filters.account

      if (this.table.allSelected) {
        this.filters.params.ids = Array.isArray(this.table.unSelected) && this.table.unSelected.map(i => i.id)
      } else {
        this.filters.params.ids = Array.isArray(this.table.selected) && this.table.selected.map(i => i.id)
      }

      this.cannotImportXls = !!(this.filters.params.allSelected || (Array.isArray(this.filters.params.ids) && this.filters.params.ids.length))
      this.cannotStoreLocation = !!(this.filters.params.allSelected || (Array.isArray(this.filters.params.ids) && this.filters.params.ids.length))

      let account = Array.isArray(this.accounts.data) && this.accounts.data.find(a => a.name === this.filters.account)
      this.filters.params.accountName = account && account.account_name
    },
    /**
     * Prepara os dados e redireciona para a tela de atualização em massa de acordo com a ação escolhida
     */
    dispatchBulk() {
      this.updateParamsFilters()

      const params = {
        ...this.appliedParams,
        ...this.appliedFilters,
        action: this.table.action,
      }
      const query = {
        tab: 'local-post',
        action: this.table.action,
      }
      this.$router.push({ name: 'location-bulk', query, params })
    },
    /**
     * Adiciona e remove os ids dos locais que serão atualizados de acordo com a flag de selecionar todos
     */
     toggleRowItem(item, value) {
    if (this.multiSelectPressed) {
      const currentIndex = this.locations.data.findIndex((i) => i.id === item.id);

      if (this.lastSelectedIndex !== null) {
        const start = Math.min(this.lastSelectedIndex, currentIndex);
        const end = Math.max(this.lastSelectedIndex, currentIndex);
        const range = this.locations.data.slice(start, end + 1);

        range.forEach((item) => {
          const itemIndex = this.table.selected.findIndex(i => i.id === item.id);
          if (value && itemIndex < 0) {
            this.table.selected.push(item);
          } else if (!value && itemIndex > -1) {
            this.table.selected.splice(itemIndex, 1);
          }
        });
      } else {
        this.table.selected.push(item);
      }

      this.lastSelectedIndex = currentIndex;
    } else {
      if (this.table.allSelected) {
        const itemIndex = this.table.unSelected.findIndex(i => i.id === item.id)
        if (!value && itemIndex < 0) {
          this.table.unSelected.push(item)
        } else if (value && itemIndex > -1) {
          this.table.unSelected.splice(itemIndex, 1)
        }
      } else {
        const itemIndex = this.table.selected.findIndex(i => i.id === item.id)
        if (value && itemIndex < 0) {
          this.table.selected.push(item)
        } else if (!value && itemIndex > -1) {
          this.table.selected.splice(itemIndex, 1)
        }
      }
      this.lastSelectedIndex = null;
    }
    this.updateParamsFilters();
  },

    showNotification(message, color) {
      this.snackbar.text = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    applyFilters() {
      this.resetTableData()
      this.page = 1
      this.fetchLocations()
    },

    fetchLocations: debounce(async function () {
      this.updateUrl()
      const sortBy = this.sortBy && this.sortBy.toString().includes('.')
        ? this.sortBy.toString().split('.')[1]
        : this.sortBy

      let payloadFilters = {
        account: this.filters.account,
        search: this.filters.search,
      }
      if (this.filters.state && this.filters.state.length > 0) {
        payloadFilters['state'] = this.filters.state
      }
      if (this.filters.city && this.filters.city.length > 0) {
        payloadFilters['city'] = this.filters.city
      }
      if (this.filters.search) {
        payloadFilters['search'] = this.filters.search
      }

      this.appliedFilters = payloadFilters
      this.updateParamsFilters()
      this.appliedParams = this.filters.params

      const payload = {
        ...payloadFilters,
        limit: this.limit,
        page: this.page,
        sort_direction: this.sortDesc ? 'desc' : 'asc',
        sort: sortBy,
      }

      this.table.loading = true
      await this.$store.dispatch('locations/getLocations', payload).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar os locais.')
        this.showNotification(message, 'red darken-2')
        this.table.loading = false
      })
      this.table.loading = false
    }, 500),

    async fetchAccounts() {
      this.loadingAccounts = true
      await this.$store.dispatch('accounts/getAccounts', { limit: 0 }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as contas.')
        this.showNotification(message, 'red darken-2')
        this.loadingAccounts = false
      })
      this.loadingAccounts = false
      await this.fetchLocationStates()
      await this.fetchLocationCities()
    },

    async fetchLocationStates(search) {
      if (!this.filters.account) {
        return
      }
      this.loadingStates = true
      await this.$store.dispatch('locations/getStates', {
        limit: 100,
        search: search,
        account: this.filters.account,
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar os estados.')
        this.showNotification(message, 'red darken-2')
        this.loadingStates = false
      })
      this.loadingStates = false
    },

    async fetchLocationCities(search) {
      if (!this.filters.account || !Array.isArray(this.filters.state) || this.filters.state.length === 0) {
        return
      }

      this.loadingCities = true
      await this.$store.commit('locations/setLocationCities', [])
      await this.$store.dispatch('locations/getCities', {
        limit: 0,
        search: search,
        account: this.filters.account,
        states: this.filters.state
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as cidades.')
        this.showNotification(message, 'red darken-2')
        this.loadingCities = false
      })
      this.loadingCities = false
    },

    resetTableData() {
      this.table.action = null
      this.table.selected = []
      this.table.unSelected = []
      this.table.allSelected = false
    },
    /**
     * Atualiza a url com os devidos filtros
     */
    updateUrl: debounce(function () {
      const url = new URL(window.location.href)
      let params = new URLSearchParams(url.search)
      params = this.handleUrlParam('state', this.filters.state, params)
      params = this.handleUrlParam('q', this.filters.search, params)
      params = this.handleUrlParam('account', this.filters.account, params)
      params = this.handleUrlParam('city', this.filters.city, params)
      params = this.handleUrlParam('page', this.page, params)
      params = this.handleUrlParam('sort_by', this.sortBy, params)
      params = this.handleUrlParam('sort_desc', this.sortDesc, params)

      if (Array.from(params.values()).length > 0) {
        const newUrl = window.location.pathname + '?' + params.toString()
        this.$router.history.push(newUrl)
      }
    }, 500),
    /**
     * Lê os parâmetros da url e preenche os dados do componente de acordo
     */
    setValuesFromUrl() {
      const url = new URL(window.location.href)
      let params = new URLSearchParams(url.search)
      this.filters.state = (params.get('state') || '').split(',').filter(v => v !== null && v !== '' && v !== false)
      this.filters.search = params.get('q')
      this.filters.account = params.get('account')
      this.filters.city = (params.get('city') || '').split(',').filter(v => v !== null && v !== '' && v !== false)
      this.table.page = parseInt(params.get('page')) || 1
      this.page = parseInt(params.get('page')) || 1
      this.table.sortBy = this.getSortBy(params.get('sort_by'))
      this.sortBy = this.getSortBy(params.get('sort_by'))
      this.table.sortDesc = params.get('sort_desc') === 'true'
      this.sortDesc = params.get('sort_desc') === 'true'
    },
    /**
     * Garante que um parâmetro esteja preenchido antes de colocá-lo na url
     */
    handleUrlParam(name, value, params) {
      if (isNotEmpty(value)) {
        params.set(name, value)
      } else {
        params.delete(name)
      }
      return params
    },

    getSortBy(sortBy) {
      const exists = this.table.headers.find(h => h.value === sortBy)
      return exists ? sortBy : null
    },

    onStateKeyUp: debounce(function () {
      this.fetchLocationStates()
    }, 300),

    onClickRow(location) {
      // this.$router.push('/location/' + location.id)
      const route = this.$router.resolve({ path: '/location/' + location.id });
      // let route = this.$router.resolve('/link/to/page'); // This also works.
      window.open(route.href, '_blank');
    },
  },
  watch: {
    $route() {
      this.setValuesFromUrl()
    },
    createExportLocation(data, oldDtada) {
      if (data && data !== oldDtada) {
        if (get(data, 'tasks[0].id')) {
          this.$router.push(`/task/${get(data, 'tasks[0].id')}`)
        } else {
          this.showNotification('Nenhuma tarefa foi criada.', 'red darken-2')
        }
      }
    },
    locations(locations) {
      if (this.table.allSelected && locations && Array.isArray(locations.data)) {
        this.table.selected = arrayUnique(this.table.selected.concat(locations.data))
          .filter(item => !this.table.unSelected.find(i => i.id === item.id))
      }
    },
    accounts(data) {
      const url = new URL(window.location.href)
      let params = new URLSearchParams(url.search)
      if (data && Array.isArray(data.data) && data.data.length > 0 && !params.get('account')) {
        this.filters.account = data.data[0].name
      }
    },
    searchState(search) {
      if (search) {
        this.onStateKeyUp(search)
      }
    },
    'filters.state': function () {
      this.fetchLocationCities()
    },
    'filters.account': async function (newVal, oldVal) {
      if (newVal !== oldVal && newVal && oldVal) {
        this.resetTableData()
        this.page = 1
        this.filters.state = null
        this.filters.city = null
        await this.$store.dispatch('locations/clearCitiesAndStates')
        await this.fetchLocationStates()
        await this.fetchLocationCities()
        this.updateUrl()
      }
    },
    'table.allSelected': function (val) {
      if (val === false) {
        this.resetTableData()
      }
      this.updateParamsFilters()
    },
    'table.options': function (options, oldOptions) {
      const sortDesc = options.sortDesc[0]
      let sortBy = options.sortBy[0]
      if ((sortBy || '').includes('formatted')) {
        sortBy = sortBy.split('_formatted')[0]
      }

      this.sortBy = sortBy
      this.sortDesc = sortDesc
      this.page = options.page

      const shouldFetchLocations = function () {
        if (options.page !== oldOptions.page) {
          return true
        }
        if (options.sortBy !== oldOptions.sortBy) {
          return true
        }
        return options.sortDesc !== oldOptions.sortDesc;
      }

      if (this.filters.account && shouldFetchLocations()) {
        this.fetchLocations()
      }
      this.updateUrl()
    },
    user: function (user) {
      const permissions = user.permissions
      Array.isArray(permissions) && permissions.forEach(item => {
        if (item.name === 'Adicionar locais') {
          this.hasStoreLocationPermission = true
        }
      })
    }
  },
}
</script>

<style>
.v-data-table__wrapper {
  cursor: pointer
}

@media screen and (max-width:1366px) {
  .mr-media-small {
    margin-right: 35px;
  }
}

@media screen and (max-width:1920px) {
  .mr-media-medium {
    margin-right: 35px;
  }
}
</style>
